import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';
import { EUILanguage, AppSettingService } from '../app.service';
import { UserInfoService } from '../user/user-info.service';

export type TSkillProvider = "onet" | "textkernel" | "";

export interface ISkill {
	SkillId: string,
	SkillName: string,
	SkillProvider: TSkillProvider
}

export interface IMySkill {
	Description: string,
	SkillId: string,
	SkillName: string,
	SkillProvider: string,
	Source: string,
	ProfessionalHistories: any[],
	Ranking: number,
	YearExperience: number,
	rankingStyle: string,
	rankingName: string
}

export interface ITargetOccupation {
	TargetOccupationId: string,
	OnetCode: string,
	OccupationName: string,
	CareerId: string,
	JobKey: string,
	JobProvider: string,
	Job: IJourneyJob
}

export interface ISaveTargetOccupation {
	OnetCode: string,
	OccupationName: string,
	CareerId: string,
	Job: any
}

export interface IJourneyJob {
	Title: string,
	Snippet: string,
	Location: string,
	Url: string,
	Provider: string,
	JobKey: string,
	OnetCodes: string[],
	MatchPercent?: number,
	//these variables are created locally
	jobUId: number,
	jobImage: string,
}

export type TTrainingProvider = "careeronestop" | "coursera";

export interface ITraining {
	DataProvider: TTrainingProvider,
	ImageUrl: string,
	Title: string,
	TrainingUrl: string,
	//careeronestop data
	SchoolName: string,
	City: string,
	State: string,
	ZipCode: string,
	Phone: string,
	ProgramLength: string[],
	// coursera data
	Partners: string[],
	ProductRatings: string,
	AvgProductRating: string[],
	Skills: string[],
	ProductDifficultyLevel: string,
	ProductType: string,
	ProductDuration: string
}

export interface ITrainingFilter {
	Skill: ISkill,
	IsGap: boolean,
	ZipCode: string,
	Providers: {
		Offset: number,
		Provider: TTrainingProvider,
		Status: string,
		DownloadComplete: boolean
	}[],
	Limit: number
}

export interface ITrainingResult {
	Filter: ITrainingFilter,
	DownloadAllComplete: boolean,
	Trainings: ITraining[]
}

export interface ITargetOccupationMatchPercent {
	JobKey: string,
	OnetCode: string,
	Require: number,
	Gap: number,
	MatchPercent: number
}

export class TrainingResult {
	AvailableProviders: TTrainingProvider[] = ["careeronestop", "coursera"];
	Filter: ITrainingFilter;
	Trainings: ITraining[] = [];
	InitLoading: boolean = false;
	Downloading: boolean = false;
	DownloadAllComplete: boolean = false;
	NoData: boolean = false;

	Init(skill: ISkill, gap:boolean, zipCode: string, providers: TTrainingProvider[]): void {
		this.Filter = {
			Skill: skill,
			IsGap: gap,
			ZipCode: zipCode,
			Providers: [],
			Limit: 10
		};
		for (let i = 0; i < providers.length; i++) {
			this.Filter.Providers.push
				(
					{
						Offset: 0,
						Provider: providers[i],
						Status: "",
						DownloadComplete: false
					}
				);
		}
		this.Trainings = [];
		this.InitLoading = true;
		this.Downloading = false;
		this.DownloadAllComplete = false;
		this.NoData = false;
	}
}

export interface ISkillGapStatus {
	HasSkill: boolean,
	HasOccupation: boolean
}


@Injectable({
	providedIn: 'root'
})
export class SkillAPIService {
	ScoreChanged: any = null;

	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient
	) {
		this.ScoreChanged = new Rx.BehaviorSubject(1);
	}

	private matcherurl = AppSettingService.appConfig.JourneyAPIUrl + "SkillsMatcher";
	private gapurl = AppSettingService.appConfig.JourneyAPIUrl + "SkillsGap";

	//#region skill question/answer
	getSkillQuestions(lang: EUILanguage): Rx.Observable<any> {
		return this.http.get<any>(
			this.matcherurl + "/Question/" + encodeURIComponent(EUILanguage[lang]),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	// if queueFailure, server may return 202 to indicate recommended soc is not available
	saveSkillAnswers(answers: any): Rx.Observable<any> {
		// update score change event
		this.ScoreChanged.next(this.ScoreChanged.getValue() + 1);

		let queueFailure: boolean = true;
		if (this.userInfo.isAnonymous()) {
			queueFailure = false;
		}
		return this.http.post<any>(
			this.matcherurl + "/Answer?institutionCode=" + encodeURIComponent(this.userInfo.getInstitutionCode()) + "&queueFailure=" + queueFailure.toString() + "&userEmail=" + encodeURIComponent(this.userInfo.anonymousEmail),
			answers,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getSkillAnswers(): Rx.Observable<any> {
		return this.http.get<any>(
			this.matcherurl + "/Answer",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(map(response => {
			if (response.SkillAnswer != null) {
				return response.SkillAnswer;
			}
			else {
				throw new Error("No skill answers are found.");
			}
		}));
	}

	getSkillAnswerMySkill(skillAnswerId: string = ""): Rx.Observable<any> {
		skillAnswerId = skillAnswerId == null ? "" : skillAnswerId;
		return this.http.get<any>(
			this.matcherurl + "/Answer/MySkill?skillAnswerId=" + encodeURIComponent(skillAnswerId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getSkillResultUser(userGuid: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.matcherurl + "/Result/" + encodeURIComponent(userGuid),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveSkillAnswerMySkill(skills:any): Rx.Observable<any> {
		return this.http.post<any>(
			this.matcherurl + "/Answer/MySkill",
			skills,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getSkillResult(): Rx.Observable<any> {
		return this.http.get<any>(
			this.matcherurl + "/Result",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getOccupationMatches(): Rx.Observable<any> {
		return this.http.get<any>(
			this.matcherurl + "/Occupation",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(map(response => {
			if (response.RecommendedOccupation != null) {
				return response.RecommendedOccupation;
			}
			else {
				throw new Error("No recommended skill occupations are found.");
			}
		}));
	}

	shareSkillResult(answers: any): Rx.Observable<any> {
		return this.http.post<any>(
			this.matcherurl + "/Result/Share",
			answers,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	downloadSkillResult(): Rx.Observable<Blob> {
		return this.http.get<any>(
			this.matcherurl + "/Result/Share/pdf",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) },
				responseType: 'blob' as 'json' 
			}
		);
	}
	//#endregion skill question/answer

	//#region person skills
	getMySkills(): Rx.Observable<any> {
		return this.http.get<any>(
			this.gapurl + "/MySkills",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveMyManualSkill(skillId: string, yearExperience:number, ranking: number): Rx.Observable<any> {
		return this.http.post<any>(
			this.gapurl + "/MySkills/Manual/" + skillId + "/" + yearExperience.toString() + "/" + ranking.toString(),
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getMyManualSkills(): Rx.Observable<any> {
		return this.http.get<any>(
			this.gapurl + "/MySkills/Manual",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	deleteMySkill(skillId: string, isAcquiredSkill: boolean): Rx.Observable<any> {
		return this.http.delete<any>(
			this.gapurl + "/MySkills/" + skillId + "/" + isAcquiredSkill.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	editMySkill(skillId: string, isAcquiredSkill: boolean, yearExperience: number, ranking: number): Rx.Observable<any> {
		return this.http.put<any>(
			this.gapurl + "/MySkills/" + skillId + "/" + isAcquiredSkill.toString() + "/" + yearExperience.toString() + "/" + ranking.toString(),
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	searchSkillGapSkills(provider: TSkillProvider, keyword: string, limit: number, offset: number): Rx.Observable<any> {
		return this.http.get<any>(
			this.gapurl + "/Skills?provider=" + encodeURIComponent(provider.toString()) + "&keyword=" + encodeURIComponent(keyword) + "&limit=" + limit.toString() + "&offset=" + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#endregion person skills

	//#region taregt occupation
	getTargetOccupationMatchPercentById(targetOccupationId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.gapurl + "/TargetOccupation/MatchPercent/" + encodeURIComponent(targetOccupationId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getTargetOccupationMatchPercent(job: IJourneyJob): Rx.Observable<any> {
		return this.http.post<any>(
			this.gapurl + "/TargetOccupation/MatchPercent",
			job,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getTargetOccupationGap(job: IJourneyJob, targetOccupationId: string): Rx.Observable<any> {
		return this.http.post<any>(
			this.gapurl + "/TargetOccupation/Gap?targetOccupationId=" + encodeURIComponent(targetOccupationId),
			job,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getTargetOccupationGapById(targetOccupationId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.gapurl + "/TargetOccupation/Gap/" + targetOccupationId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveTargetOccupation(target: ISaveTargetOccupation): Rx.Observable<ITargetOccupation> {
		return this.http.post<any>(
			this.gapurl + "/TargetOccupation",
			target,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	deleteTargetOccupation(targetOccupationId: string): Rx.Observable<any> {
		return this.http.delete<any>(
			this.gapurl + "/TargetOccupation/" + encodeURIComponent(targetOccupationId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getTargetOccupations(limit: number, offset: number): Rx.Observable<any> {
		return this.http.get<any>(
			this.gapurl + "/TargetOccupation?limit=" + limit.toString() + "&offset=" + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	SearchTargeJobs(provider: string, jobKey: string): Rx.Observable<ITargetOccupation[]> {
		return this.http.get<any>(
			this.gapurl + "/TargetOccupation/Job/" + encodeURIComponent(provider) + "/" + encodeURIComponent(jobKey),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	
	getTargetOccupationSkills(): Rx.Observable<any> {
		return this.http.get<any>(
			this.gapurl + "/TargetOccupation/Skills",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#endregion taregt occupation

	//#region recommended training
	getSkillTraining(filter: ITrainingFilter): Rx.Observable<ITrainingResult> {
		return this.http.post<any>(
			this.gapurl + "/Trainings",
			filter,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#endregion recommended training

	getSkillGapStatus(): Rx.Observable<ISkillGapStatus> {
		return this.http.get<any>(
			this.gapurl + "/Status",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}

